/**
 * HorizontalBar
 *
 * A labelled horizontal progress/value bar — the breakdown-bar pattern used
 * across the analytics tools (section reach, price distribution, CTA
 * attribution in the Engagement tool; tag / country breakdowns in Redirects).
 *
 * Promoted to a shared component so the inline copies in FunnelTab and
 * elsewhere can converge on one implementation.
 *
 * Two layouts:
 *   - default (inline): fixed-width label column, then bar, then value. Best
 *     for short, uniform labels.
 *   - labelAbove (stacked): label sits on its own line above a full-width bar
 *     and may wrap. Best for long/variable labels like URL paths or hostnames.
 *
 * `sublabel` adds a second, quieter line under the label, cut with an ellipsis when it is
 * too long (the full text is on hover via `labelTitle`). For a label with two parts — a
 * source and its campaign — rather than one very long line.
 *
 * Usage:
 *   <HorizontalBar label="booklet" value={45} max={120} color="#4299E1" />
 *   <HorizontalBar label="On-site: /conveyancers/cpd-training" value={3} max={9} labelAbove />
 */

import React from "react";
import PropTypes from "prop-types";
import "./HorizontalBar.scss";

const HorizontalBar = ({
  label,
  value,
  max,
  total,
  color = "#6366f1",
  suffix = "",
  labelWidth = 160,
  valueWidth = 60,
  showPercentInBar = true,
  trailing,
  onClick,
  active = false,
  labelAbove = false,
  sublabel,
  labelTitle,
  className = "",
}) => {
  // Two scaling modes:
  //   • share-of-total (pass `total` = the sum of the group's values): each bar
  //     is value/total, so the bars total 100% — a part-to-whole/composition
  //     view. The default for most breakdown charts.
  //   • proportional-to-max (pass only `max` = the largest value): each bar is
  //     value/max, so the biggest fills the track (reads 100%) and the rest are
  //     relative to it — best for emphasising the leader / comparing magnitudes.
  // When `total` is given it wins. In share mode the in-bar % now reads the
  // share (not "100%"), so it stays useful — we only suppress it to avoid a
  // duplicate when the value column already carries a `suffix` (e.g. " (32%)").
  const shareMode = typeof total === "number" && total > 0;
  const denom = shareMode ? total : max;
  const width = denom > 0 ? Math.round((value / denom) * 100) : 0;
  const showInBar = showPercentInBar && !(shareMode && suffix);
  const clickable = typeof onClick === "function";

  const interactionProps = {
    onClick,
    role: clickable ? "button" : undefined,
    tabIndex: clickable ? 0 : undefined,
    onKeyDown: clickable
      ? (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(e);
          }
        }
      : undefined,
  };

  const rootClass = `hbar ${labelAbove ? "hbar--stacked" : ""} ${
    clickable ? "hbar--clickable" : ""
  } ${active ? "hbar--active" : ""} ${className}`;

  // Stacked, with the percentage already in the value column, the bar carries no text of
  // its own and can be slim: it sits under a label that may now be two lines.
  const slim = labelAbove && !showInBar;
  const track = (
    <div className={`hbar__track${slim ? " hbar__track--slim" : ""}`}>
      <div
        className="hbar__fill"
        style={{
          width: `${width}%`,
          background: `linear-gradient(90deg, ${color}dd, ${color})`,
        }}
      >
        {showInBar && width > 15 && (
          <span className="hbar__fill-pct">{width}%</span>
        )}
      </div>
    </div>
  );

  const valueNode = (
    <>
      {value.toLocaleString()}
      {suffix}
    </>
  );

  // One line as before; with a sublabel, the label above and the quieter line beneath it.
  const labelNode = sublabel ? (
    <>
      <span className="hbar__label-main">{label}</span>
      <span className="hbar__sublabel">{sublabel}</span>
    </>
  ) : label;

  if (labelAbove) {
    return (
      <div className={rootClass} {...interactionProps}>
        <div className="hbar__top">
          <div className="hbar__label hbar__label--above" title={labelTitle}>{labelNode}</div>
          <div className="hbar__value hbar__value--above">{valueNode}</div>
        </div>
        {track}
        {trailing}
      </div>
    );
  }

  return (
    <div className={rootClass} {...interactionProps}>
      <div className="hbar__label" style={{ width: labelWidth }} title={labelTitle}>
        {labelNode}
      </div>
      {track}
      <div className="hbar__value" style={{ width: valueWidth }}>
        {valueNode}
      </div>
      {trailing}
    </div>
  );
};

HorizontalBar.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  /** Largest value in the group — proportional-to-max scaling (biggest bar = 100%). */
  max: PropTypes.number,
  /** Sum of the group's values — share-of-total scaling (bars total 100%). Wins over `max`. */
  total: PropTypes.number,
  color: PropTypes.string,
  /** Appended after the numeric value (e.g. " (32%)") */
  suffix: PropTypes.string,
  labelWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  valueWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  showPercentInBar: PropTypes.bool,
  /** Optional node rendered after the value column (e.g. a Badge) */
  trailing: PropTypes.node,
  onClick: PropTypes.func,
  active: PropTypes.bool,
  /** Stack the (wrapping) label above a full-width bar — best for long labels */
  labelAbove: PropTypes.bool,
  /** A second, quieter line under the label; cut with an ellipsis when too long. */
  sublabel: PropTypes.node,
  /** The label's full text, shown on hover (use with `sublabel`, which may be cut). */
  labelTitle: PropTypes.string,
  className: PropTypes.string,
};

export default HorizontalBar;
